import {
  DragEvent as SyntheticDragEvent,
  useEffect,
  useState,
  useRef,
  ChangeEvent
} from 'react'
import { UploadIcon } from '@heroicons/react/outline'
import { CheckCircleIcon } from '@heroicons/react/solid'
import { ErrorMessage, useFormikContext } from 'formik'

import { FormValues } from '.'
import { Link } from '@components/Link'
import { InvalidMessage } from '@components/UI/Form/InvalidMessage'
import axios from 'axios'
import { createClient } from '@services/api'
import toast from 'react-hot-toast'
import { useRouter } from 'next/router'
import { motion, useMotionTemplate, useSpring } from 'framer-motion'

export function UploadInspection() {
  const { query } = useRouter()
  const hasIntegration = Boolean(query.ref && query.inspection)
  const progressAmount = useSpring(0)
  const progress = useMotionTemplate`${progressAmount}%`
  const fileInput = useRef<HTMLInputElement>(null)
  const isMounted = useRef(false)
  const {
    values,
    errors,
    touched,
    setFieldValue,
    setFieldError
  } = useFormikContext<FormValues>()
  const [isUploading, setIsUploading] = useState(false)
  const [filename, setFilename] = useState('')
  const [isDragHovered, setIsDragHovered] = useState(false)

  useEffect(() => {
    function preventDefault(e: DragEvent) {
      e.preventDefault()
    }

    window.addEventListener('dragover', preventDefault)

    return () => {
      window.removeEventListener('dragover', preventDefault)
    }
  })

  useEffect(() => {
    if (values.inspection) {
      setFilename(values.inspection.name)
      void uploadInspection()
    }

    return () => {
      isMounted.current = true
    }
  }, [values.inspection])

  function onDragOver() {
    setIsDragHovered(true)
  }

  function onDragLeave() {
    setIsDragHovered(false)
  }

  function onDrop(event: SyntheticDragEvent<HTMLLabelElement>) {
    event.preventDefault()
    event.stopPropagation()

    setIsDragHovered(false)

    setFilename(event.dataTransfer.files?.[0]?.name)
    setFieldValue('inspection', event.dataTransfer.files?.[0])
  }

  async function uploadInspection() {
    setIsUploading(true)

    if (errors.inspection) {
      setFieldError('inspection', '')
    }

    if (values.inspectionKey && values.inspection) {
      setFieldValue('inspection', '')
      setFieldValue('inspectionKey', '')
    }

    const canUseIntegration = Boolean(
      hasIntegration && values.integrationConsent
    )

    let inspectionKey: string | undefined
    function onUploadProgress(event: ProgressEvent) {
      if (event.lengthComputable) {
        const percentComplete = event.loaded / event.total
        if (percentComplete === 1) {
          setIsUploading(false)
        }
        progressAmount.set(percentComplete * 100)
      }
    }

    // If we are to retrieve the inspection from a partner, skip uploading a PDF
    if (!canUseIntegration) {
      try {
        const { data: uploadInfo } = await createClient().get(
          '/checkout/inspection-upload-url'
        )
        const inspectionUploadData = new FormData()

        for (const [field, value] of Object.entries(uploadInfo.fields)) {
          inspectionUploadData.set(field, value as string)
        }

        inspectionUploadData.set('file', values.inspection as File)

        await axios.post(uploadInfo.url, inspectionUploadData, {
          onUploadProgress
        })

        inspectionKey = uploadInfo.fields.key
        setFieldValue('inspectionKey', inspectionKey)

        setIsUploading(false)
      } catch (error) {
        console.error(error)
        toast.error(
          "There was a problem uploading your inspection. Please reach out to our team, and we'll get in touch ASAP."
        )

        // if (!isMounted.current) {
        setIsUploading(false)
        // }
      }
    }
  }

  return (
    <div className="block bg-white p-8 rounded shadow-sm" id="inspection">
      <h2 className="text-2xl font-black tracking-tight mb-8">
        Upload Single Family Home Inspection
      </h2>
      <p className="mb-8">
        Upload a PDF of your home inspection. If the file is in a{' '}
        <Link
          type="outbound"
          href="https://intercom.help/majordomo/en/articles/3213854-what-if-my-inspection-is-not-a-pdf"
        >
          different format
        </Link>
        , you have{' '}
        <Link
          type="outbound"
          href="https://intercom.help/majordomo/en/articles/3213851-what-if-my-inspection-is-more-than-one-file"
        >
          multiple files
        </Link>
        , or it's for a{' '}
        <Link
          type="outbound"
          href="https://intercom.help/majordomo/en/articles/3578252-do-you-create-domoreports-for-multi-family-properties"
        >
          multi-family property
        </Link>
        , contact us via chat.
      </p>
      <label
        className={`rounded border-dashed border-4 border-gray-200 text-gray-200 w-full h-48 flex flex-col justify-center items-center cursor-pointer transition-colors ${
          isDragHovered
            ? 'bg-blue-100'
            : errors.inspectionKey && touched.inspectionKey
            ? 'bg-red-50 border-red-200 text-red-200'
            : 'bg-white'
        }`}
        htmlFor="inspection-pdf"
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
      >
        <input
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            if (event.currentTarget.files?.[0]) {
              setFieldValue('inspection', event.currentTarget.files?.[0])
            }
          }}
          ref={fileInput}
          className="hidden"
          type="file"
          name="inspection"
          id="inspection-pdf"
          accept=".pdf"
        />
        {filename ? (
          <div
            className={`relative rounded-full ${
              isUploading
                ? 'bg-gray-200 text-gray-600'
                : 'bg-green-200 text-green-600'
            } p-2 font-bold mb-2 flex`}
          >
            <CheckCircleIcon width={20} height={20} className="z-10" />
            {isUploading ? (
              <>
                <span className="mx-2 z-10">Uploading Inspection...</span>
                <motion.div
                  className="absolute top-0 left-0 h-full rounded-full animate-pulse bg-gray-300"
                  style={{ width: progress, minWidth: 40 }}
                />
              </>
            ) : (
              <span className="mx-2 z-10">{filename}</span>
            )}
          </div>
        ) : (
          <UploadIcon width={48} height={48} className="mb-4" />
        )}
        <p className="text-center text-gray-700">
          {filename.length === 0 && 'Drop your inspection PDF here or '}
          <label
            htmlFor="inspection-pdf"
            className={`hover:underline cursor-pointer ${
              filename.length > 0 ? 'text-gray-400' : 'text-brand'
            }`}
          >
            {filename.length > 0 ? 'Select a Different File' : 'browse'}
          </label>
        </p>
        <ErrorMessage
          component={InvalidMessage}
          name="inspection"
          className="text-red-500"
        />
      </label>
    </div>
  )
}
